import React from 'react';
import { statusLabel, statusColors, initials } from '../lib/utils';

// ── Brand colors
export const B = {
  navy: '#0F172A', amber: '#F59E0B', success: '#16A34A',
  warning: '#D97706', info: '#2563EB', danger: '#DC2626', muted: '#64748B',
};

// ── Avatar
export function Avatar({ name = '?', size = 36, color = B.amber }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: color + '22', border: `2px solid ${color}44`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.floor(size * 0.35), fontWeight: 700, color,
      fontFamily: "'Barlow Condensed', sans-serif",
    }}>
      {initials(name)}
    </div>
  );
}

// ── Status badge
export function Badge({ status }) {
  const sc = statusColors(status);
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
      background: sc.bg, color: sc.color, whiteSpace: 'nowrap',
    }}>
      {statusLabel(status)}
    </span>
  );
}

// ── Progress bar
export function ProgressBar({ value = 0, height = 6, color = B.amber }) {
  return (
    <div style={{ background: '#E2E8F0', borderRadius: height, overflow: 'hidden', height }}>
      <div style={{
        width: `${Math.min(100, Math.max(0, value))}%`, height: '100%',
        background: color, borderRadius: height, transition: 'width .4s',
      }} />
    </div>
  );
}

// ── Card
export function Card({ children, style = {}, onClick }) {
  const [hov, setHov] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={onClick ? () => setHov(true) : undefined}
      onMouseLeave={onClick ? () => setHov(false) : undefined}
      style={{
        background: '#fff', border: `1px solid ${hov ? '#CBD5E1' : '#E2E8F0'}`,
        borderRadius: 12, padding: '1.25rem',
        boxShadow: hov ? '0 2px 8px rgba(0,0,0,.07)' : 'none',
        cursor: onClick ? 'pointer' : undefined,
        transition: 'border-color .15s, box-shadow .15s', ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Button
export function Btn({ children, onClick, variant = 'outline', size = 'md', style = {}, disabled, type = 'button' }) {
  const sizes = { sm: { fontSize: 12, padding: '5px 10px' }, md: { fontSize: 13, padding: '8px 14px' }, lg: { fontSize: 14, padding: '10px 18px' } };
  const variants = {
    primary: { background: B.amber, color: '#fff', border: 'none' },
    dark:    { background: B.navy, color: '#fff', border: 'none' },
    danger:  { background: '#FEF2F2', color: B.danger, border: '1px solid #FECACA' },
    outline: { background: '#fff', color: '#374151', border: '1px solid #D1D5DB' },
    ghost:   { background: 'transparent', color: '#6B7280', border: '1px solid transparent' },
    success: { background: '#F0FDF4', color: B.success, border: '1px solid #86EFAC' },
    info:    { background: '#EFF6FF', color: B.info, border: '1px solid #BFDBFE' },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        borderRadius: 8, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: "'Barlow', sans-serif", transition: 'opacity .15s',
        opacity: disabled ? 0.5 : 1, whiteSpace: 'nowrap',
        ...sizes[size], ...variants[variant], ...style,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = '0.85'; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
    >
      {children}
    </button>
  );
}

// ── Stat card
export function Stat({ label, value, sub, accent = B.navy }) {
  return (
    <div style={{ background: '#F8FAFC', borderRadius: 10, padding: '1rem', border: '1px solid #E2E8F0' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: accent, fontFamily: "'Barlow Condensed', sans-serif", lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Section header
export function SH({ title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
      <h3 style={{ fontSize: 13, fontWeight: 800, color: B.navy, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '.05em', textTransform: 'uppercase', margin: 0 }}>{title}</h3>
      {action}
    </div>
  );
}

// ── Modal
export function Modal({ title, onClose, children, footer, wide = false }) {
  React.useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 50, zIndex: 999, overflowY: 'auto' }}
    >
      <div style={{ background: '#fff', borderRadius: 16, width: wide ? 720 : 580, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,.3)', margin: '0 auto 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #E2E8F0' }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0, fontFamily: "'Barlow Condensed', sans-serif", color: B.navy }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94A3B8', lineHeight: 1, padding: '2px 6px' }}>✕</button>
        </div>
        <div style={{ padding: '1.5rem' }}>{children}</div>
        {footer && <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>{footer}</div>}
      </div>
    </div>
  );
}

// ── Form field
export function Field({ label, children, required, span2 = false }) {
  return (
    <div style={{ marginBottom: '1rem', ...(span2 ? { gridColumn: '1 / -1' } : {}) }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>
        {label}{required && <span style={{ color: B.danger }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// ── Input / Select / Textarea
const iStyle = { width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: "'Barlow', sans-serif", color: B.navy, outline: 'none', background: '#fff', boxSizing: 'border-box' };

export const Input = React.forwardRef((props, ref) => (
  <input ref={ref} style={iStyle} {...props}
    onFocus={e => { e.target.style.borderColor = B.amber; e.target.style.boxShadow = '0 0 0 3px rgba(245,158,11,.12)'; }}
    onBlur={e => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
  />
));

export const Select = ({ children, ...props }) => (
  <select style={iStyle} {...props}
    onFocus={e => { e.target.style.borderColor = B.amber; }}
    onBlur={e => { e.target.style.borderColor = '#D1D5DB'; }}
  >{children}</select>
);

export const Textarea = (props) => (
  <textarea style={{ ...iStyle, minHeight: 72, resize: 'vertical' }} {...props}
    onFocus={e => { e.target.style.borderColor = B.amber; e.target.style.boxShadow = '0 0 0 3px rgba(245,158,11,.12)'; }}
    onBlur={e => { e.target.style.borderColor = '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
  />
);

// ── Toast
export function Toast({ message, type = 'success', onDone }) {
  React.useEffect(() => { const t = setTimeout(onDone, 3500); return () => clearTimeout(t); }, [onDone]);
  const colors = {
    success: { bg: '#F0FDF4', border: '#86EFAC', color: '#15803D' },
    error:   { bg: '#FEF2F2', border: '#FECACA', color: '#991B1B' },
    info:    { bg: '#EFF6FF', border: '#BFDBFE', color: '#2563EB' },
  };
  const c = colors[type] || colors.success;
  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, background: c.bg, border: `1px solid ${c.border}`, color: c.color, padding: '12px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,.15)', display: 'flex', alignItems: 'center', gap: 8, maxWidth: 340 }}>
      {type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'} {message}
    </div>
  );
}

// ── Spinner
export function Spinner({ size = 32 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem' }}>
      <div style={{ width: size, height: size, border: `3px solid #E2E8F0`, borderTop: `3px solid ${B.amber}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ── Page header
export function PageHeader({ title, sub, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: 10 }}>
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 800, fontFamily: "'Barlow Condensed', sans-serif", color: B.navy, letterSpacing: '.03em', margin: 0 }}>{title}</h1>
        {sub && <p style={{ fontSize: 13, color: '#94A3B8', marginTop: 2 }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

// ── Empty state
export function Empty({ icon = '📋', title, sub, action }) {
  return (
    <Card style={{ textAlign: 'center', padding: '3rem' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: B.navy, marginBottom: 6 }}>{title}</div>
      {sub && <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: action ? 16 : 0 }}>{sub}</div>}
      {action}
    </Card>
  );
}

// ── Grid helpers
export const Grid2 = ({ children, style = {} }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.25rem', ...style }}>{children}</div>
);
export const Grid4 = ({ children, style = {} }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, ...style }}>{children}</div>
);
